import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import styles from "../Add Asset/styles/AddAsset.module.css";
import Info from "../Info/info";
import Upload from "../Upload/upload";
import FileIcon from "../File/file";
import { Beneficiarytrustee } from "../Beneficiarytrustee/Beneficiarytrustee";
import CredentialsMenu from "./CredentialsMenu";
import PartnerSharing, {
  EMPTY_PARTNER_SHARING,
  getPartner,
} from "../Partner Sharing/PartnerSharing";
import { isPendingApproval } from "../../../Utils/partnerApproval";
import { canEditRecord } from "../../../Utils/partnerApproval";
import {
  canEditAnything,
  changedFields,
  editableSections,
  mergeProposedIds,
  readProposals,
} from "../../../Utils/partnerPropose";
import ProposedField from "../Partner Sharing/ProposedField";
import proposed from "../Partner Sharing/styles/ProposedField.module.css";
import ViewOnly from "../../ViewOnly";
import AddBeneficiary from "./AddBeneficiary";
import AddTrustee from "./AddTrustee";
import AssetEvaluation, {
  EMPTY_EVALUATION,
} from "../Asset Evaluation/AssetEvaluation";
import {
  sortRecordsByDate,
  toInputDate,
  mergeEvaluationIntoAsset,
  mergeReadingIntoAsset,
  mergeDebtIntoAsset,
  mergeEntryIntoAsset,
  evaluationFromAsset,
  resolveAssetTypeSelection,
  canonicalAssetType,
  ASSET_TYPE_CUSTOM,
  ownerOptionsFrom,
  assetTypeGroupsFor,
  ASSET_TYPE_MENU_HEIGHT,
} from "../../../Utils/assetEvaluationUtils";
import Dropdown from "../Asset Evaluation/Dropdown";
import { getAssetTypeTranslated } from "../../../Utils/translationUtils";
import { RandomId } from "../../../Utils/RandomId";
import { UserData } from "../../../Context/DataContext";
import {
  DeleteFile,
  UploadFiles,
  downloadLocalFile,
} from "../../../Utils/UploadFile";
import { downloadFile } from "../../../Utils/UploadFile";
import Confirmation from "../../Confirmation/Confirmation";
import { OnProgress } from "../My Account/OnProgress";
import { handleIssues } from "../../../Utils/handleIssues";
import { isValidFileSize } from "../../../Utils/isValidFileSize";
import { floatMessage } from "../../../Utils/floatMessage";
import parse from "html-react-parser";
import missingFilesIcon from "./styles/img/missing_files.svg";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { Button, Checkbox, Modal } from "antd";
import useWindowDimensions from "../../Footer/footer";
import { useScrollLock } from "../../../Utils/useScrollLock";
import { useSmoothHeight } from "../../../Utils/useSmoothHeight";

/*
  `focusEvaluation` — "evaluation" or "debt" — opens the Asset Evaluation panel
  on that part and scrolls to it. Sent by the Asset Management table when a
  value cell has no history to edit inline, so the modal lands where the user
  was already trying to type.
*/
// Stable, so the evaluation panel's memos do not recompute on every render.
const NO_PROPOSED = [];

/*
  The credentials block, however it arrives.

  Stored as a JSON string, but not always a parseable one: a partner's copy of
  an asset has `credentials: ""`, because there is no sharing flag for them,
  the server sends none and the section is not even rendered on their screen.
  `JSON.parse("")` throws, and the one caller is inside an async function
  nobody awaits -- so the throw became a swallowed rejection and Save did
  nothing at all, with no error anywhere.

  An empty object is the right answer for every unparseable case: no
  credentials is exactly what is being described.
*/
const isPlainObject = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const parseCredentials = (value) => {
  if (isPlainObject(value)) return value;
  try {
    const parsed = JSON.parse(value || "{}");
    return isPlainObject(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

function EditAsset({ toEdit, onClose, openAssets, openBeneficiary, focusEvaluation }) {
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const divElementRef = useRef(null);
  const boxRef = useRef(null);
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [dublicatedcontent, setDublicatedContent] = useState(
    websiteCurrentLang.content.DublicatedText
  );
  const [openModal, setOpenModal] = useState(false);
  const [content, setContent] = useState(
    websiteCurrentLang.content.User.EditAsset.EditAsset
  );
  /*
    Partner sharing. Frontend only — not part of the save payload yet, and the
    partner's name/picture are placeholders until the backend exposes the
    relationship. See docs/partner-sharing-backend-spec.md.
  */
  /*
    Seeded from the asset being edited so the switches show what is actually
    shared. An asset saved before these flags existed has none of them, and the
    defaults then apply -- not shared, with the four details ready to go.
  */
  const [partnerSharing, setPartnerSharing] = useState({
    ...EMPTY_PARTNER_SHARING,
    ...(toEdit?.share_with_partner === undefined
      ? {}
      : {
          share_with_partner: Boolean(toEdit.share_with_partner),
          share_description: toEdit.share_description !== false,
          share_files: toEdit.share_files !== false,
          share_evaluations: toEdit.share_evaluations !== false,
          share_beneficiaries: toEdit.share_beneficiaries !== false,
          share_trusted_advisor: toEdit.share_trusted_advisor !== false,
          /*
            The edit permissions, seeded the same way -- and `=== true`, not
            `!== false`: an asset saved before these existed has granted
            nothing, and reading a missing flag as permission would hand a
            partner edit rights on every asset the customer had already
            shared.

            Carried here because this object is spread over the asset on
            save: a flag left out would be reset to its default the next time
            the customer touched the asset, quietly withdrawing a permission
            they had granted.
          */
          edit_description: toEdit.edit_description === true,
          edit_files: toEdit.edit_files === true,
          edit_evaluations: toEdit.edit_evaluations === true,
          edit_beneficiaries: toEdit.edit_beneficiaries === true,
          edit_trusted_advisor: toEdit.edit_trusted_advisor === true,
        }),
  });
  const assetEvaluationRef = useRef(null);
  useEffect(() => {
    setContent(websiteCurrentLang.content.User.EditAsset.EditAsset);
    setDublicatedContent(websiteCurrentLang.content.DublicatedText);
  }, [websiteCurrentLang]);
  const handleScroll = () => {
    const divElement = divElementRef.current;
    const scrollTop = divElement.scrollTop;
    const scrollHeight = divElement.scrollHeight;
    const clientHeight = divElement.clientHeight;
    const threshold = 10; // Adjust the threshold as needed

    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - threshold);
  };
  const { width } = useWindowDimensions();
  useEffect(() => {
    const divElement = divElementRef.current;

    // Call handleScroll once on initial load to set shadows
    handleScroll();

    divElement.addEventListener("scroll", handleScroll);

    return () => {
      divElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const userData = UserData();
  /*
    Null for a user with no partner, and the section is then not rendered at
    all: its heading and its note both name the partner, so with nobody to name
    there is nothing for it to say. See getPartner.
  */
  const partner = getPartner(userData);

  /*
    Opened to review something the partner created: every field is editable, as
    the approval interstitial promises, and only the confirming button changes
    -- saving IS approving here, so it says so.
  */
  const awaitingApproval = isPendingApproval(toEdit);
  /*
    Read-only: a partner looking at something the customer wrote.

    They may read what was shared and nothing else. Save and Delete are not
    rendered, and every control that assigns or uploads is withheld, so the
    only things left are reading and closing. Editing is possible for exactly
    one case -- their own draft the customer has not approved -- which is what
    canEditRecord decides.

    The writers in this view are stubbed too, so nothing could persist even if
    a control were missed; this is what makes that visible rather than a
    surprise.
  */
  const readOnly = !canEditRecord(userData, toEdit);

  /*
    ---- a partner with edit permission ----

    Read-only is no longer all-or-nothing. A customer can let their partner
    change particular parts of an asset (the Edit column of the sharing
    panel), and those parts stay live inside the otherwise inert modal --
    `data-viewonly-editable` is what ViewOnly leaves alone.

    Nothing saved here reaches the asset. Save becomes "propose": the changed
    fields are encrypted under the customer's key and queued against the
    record, and the customer decides. `partnerPropose` is only present in a
    customer's profile (see My Customers/CustomerDetail.jsx), so this whole
    branch is dead on the customer's own screen.
  */
  /*
    ---- the other side of the same feature ----

    What this customer's partner has asked to change about this asset, opened
    with the customer's own key. Only the customer (and the partner who wrote
    it) can read one; a value that will not decrypt is dropped rather than
    shown as ciphertext.

    `decisions` holds only the fields the customer has actually answered.
    Untouched proposals are left pending on save rather than silently
    discarded -- somebody who opened the asset to change the name should not
    throw away a suggestion they never looked at.
  */
  const [proposals, setProposals] = useState({});
  const [decisions, setDecisions] = useState({});
  useEffect(() => {
    let live = true;
    (async () => {
      const read = await readProposals(toEdit, userData.private_key);
      if (live) setProposals(read);
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toEdit?.asset_id]);

  /*
    Taking a suggestion writes it into the form, so the fields below this
    panel show what saving will keep; dropping it puts the customer's own
    value back. The decision itself is remembered either way -- that is what
    tells the save which proposals were answered.
  */
  /*
    A partner's proposal as it would actually land, which for one field is not
    what they sent.

    `beneficiaries` is a list of ids, and the list they were shown had the
    withheld ones taken out of it -- the server intersects an asset's
    beneficiaries with the beneficiaries whose own switch is on. So their
    edit covers the visible half and says nothing about the rest, and the
    rest has to survive it. Resolved here rather than at the point of
    acceptance so the panel's "New" column counts what saving would really
    keep.

    `trustees` needs none of this: the asset's own flag is the disclosure
    there, so an asset that shares its advisors shares all of them and the
    partner edited the whole list.
  */
  const partnerCouldSeeBeneficiary = (id) =>
    (userData.UserData?.beneficiaries || []).find((b) => b.user_id === id)
      ?.share_with_partner === true;

  const resolvedProposals = useMemo(() => {
    const out = {};
    for (const [field, change] of Object.entries(proposals)) {
      out[field] =
        field === "beneficiaries"
          ? {
              ...change,
              value: mergeProposedIds(
                toEdit?.beneficiaries,
                change?.value,
                partnerCouldSeeBeneficiary
              ),
            }
          : change;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, toEdit]);

  /*
    One switch, directly above the field it is about, and the field outlined
    to match. Nothing for a field nobody has proposed a change to, and
    nothing at all on the partner's own screen -- they are the ones asking.
  */
  const review = (field, label, children = null) => {
    /*
      A field inside a group that is not the one carrying its switch gets
      its label back and nothing else -- the switch beside the figure
      already answers for it. Its outline stays, through reviewMark, so a
      reader can still see which fields that one switch is about.
    */
    const lead = DECISION_LEAD[field];
    if (lead && lead !== field) return children;

    /*
      And the switch answers for the whole group, so it has to appear when
      ANY member was proposed -- a partner may have moved the date and left
      the figure alone. ProposedField decides from the entry it is handed,
      so the group's is passed under this field's name.
    */
    const group = DECISION_GROUPS[field];
    let forField = resolvedProposals;
    if (group && !resolvedProposals[field]) {
      const found = group.find((name) => resolvedProposals[name]);
      if (found) forField = { ...resolvedProposals, [field]: resolvedProposals[found] };
    }

    return (
      <ProposedField
        field={field}
        label={label}
        proposals={forField}
        decisions={decisions}
        onDecide={decideProposal}
        active={!readOnly}
        disabled={userData.isFreezed}
      >
        {children}
      </ProposedField>
    );
  };
  // The purple outline, for a box (`field`) or for a whole region (`block`).
  const reviewMark = (field, kind = "field") =>
    !readOnly && resolvedProposals[field] ? proposed[kind] : "";

  /*
    The names of the fields with a question waiting, for the evaluation panel
    to decide whether it and its history sections need to be open. Names
    only: what accepting one does stays here.
  */
  const proposedFields = useMemo(
    () => (readOnly ? [] : Object.keys(resolvedProposals)),
    [resolvedProposals, readOnly]
  );

  /*
    Fields that are two halves of one change, and are answered together.

    A valuation is the figure AND the row it writes into the history: the
    panel holds the figure, `appendEvaluationRecords` turns it into the
    record, and the customer sees both. Answered separately they can
    disagree -- refuse the value, keep the reading, and the history shows a
    figure the asset does not have. There is no sense in which a customer
    means one and not the other, so deciding either decides both.

    The debt amount and its own series are the same relationship.
  */
/*
    What "Previous" means for a panel field the form deliberately opens blank.

    `evaluationFromAsset` empties the READING fields on purpose -- the panel
    is a form for entering the NEXT valuation, so it should not arrive
    pre-filled with last time's figures. That is right when the modal is
    being used to record something, and wrong when it is being used to
    review: refusing a proposed debt amount restored the blank form instead
    of the amount the asset actually holds, so the customer was shown an
    empty box as though that were their own value. The debt input history
    beside it showed the old figure correctly, which is how it was spotted.

    So for these five, Previous is read off the record rather than off the
    starting shape. Dates go back through toInputDate, because the asset
    stores them in the stored format and the field reads the input one.
  */
  /*
    The most recent reading in a series, which is what "Previous" means.

    The asset's own `asset_value` / `debt_amount` were being used, and they
    are not reliably the latest: `keepUnlessProvided` leaves them alone when
    a save does not carry a figure, so they can sit on an older one while
    the history has moved on. The series is the record of what was read and
    when -- sorted ascending, with a created_at tiebreak, so the last entry
    is the newest.
  */
  const asList = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.startsWith("[")) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const lastReading = (records) => {
    const sorted = sortRecordsByDate(records).filter(
      (record) =>
        record && record.value !== "" && record.value !== null &&
        record.value !== undefined
    );
    return sorted.length ? sorted[sorted.length - 1] : null;
  };

  /*
    And what "New" shows, for the same fields.

    A partner can propose SEVERAL readings before the customer looks --
    two rows in the history, two dates. The scalar that comes with them
    holds whichever one the proposal happened to carry, so accepting showed
    the earlier date beside the later row. Read from the proposed series
    instead, newest entry, exactly as Previous reads from the stored one;
    the scalar is the fallback when a proposal has no series with it.
  */
  const acceptedFromProposal = {
    evaluation_date: (records) => toInputDate(lastReading(records)?.date),
    asset_value: (records) => lastReading(records)?.value,
    currency: (records) => lastReading(records)?.currency,
    debt_amount: (records) => {
      const last = lastReading(records);
      if (last == null) return undefined;
      const amount = Number(last.value);
      return Number.isNaN(amount) ? undefined : Math.abs(amount);
    },
    debt_currency: (records) => lastReading(records)?.currency,
  };

  // Which series each of those reads from.
  const SERIES_OF = {
    evaluation_date: "asset_evaluation_records",
    asset_value: "asset_evaluation_records",
    currency: "asset_evaluation_records",
    debt_amount: "asset_debt_records",
    debt_currency: "asset_debt_records",
  };

  const acceptedValue = (field) => {
    const from = acceptedFromProposal[field];
    if (from) {
      const proposedSeries = asList(resolvedProposals[SERIES_OF[field]]?.value);
      if (proposedSeries.length) {
        const value = from(proposedSeries);
        if (value !== undefined && value !== null && value !== "") return value;
      }
    }
    return resolvedProposals[field]?.value;
  };

  /*
    What "Previous" restores for a panel field the form deliberately opens
    blank.

    `evaluationFromAsset` empties the READING fields on purpose -- the panel
    is a form for entering the NEXT valuation, so it should not arrive
    pre-filled. That is right when recording and wrong when reviewing:
    refusing a proposed figure restored the blank form, so the customer was
    shown an empty box as though it were their own value.

    Each falls back to the asset's scalar when the series is empty -- an
    asset valued once, before records existed, still has one.
  */
  const previousFromAsset = {
    evaluation_date: (a) =>
      toInputDate(lastReading(a?.asset_evaluation_records)?.date) ||
      toInputDate(a?.evaluation_date) ||
      "",
    asset_value: (a) =>
      lastReading(a?.asset_evaluation_records)?.value ?? a?.asset_value ?? "",
    currency: (a) =>
      lastReading(a?.asset_evaluation_records)?.currency || a?.currency || "",
    /*
      Debt readings are stored negative -- a liability plots below the zero
      line -- while the field takes the amount owed, so the sign comes off.
    */
    debt_amount: (a) => {
      const last = lastReading(a?.asset_debt_records);
      if (last == null) return a?.debt_amount ?? "";
      const amount = Number(last.value);
      return Number.isNaN(amount) ? (a?.debt_amount ?? "") : Math.abs(amount);
    },
    debt_currency: (a) =>
      lastReading(a?.asset_debt_records)?.currency || a?.debt_currency || "",
  };

  /*
    ---- fields that are one change, and are answered once ----

    A valuation is not three decisions. It is a figure, the date it was
    taken on, the currency it is in, and the row all of that writes into
    the history -- `appendEvaluationRecords` builds the row FROM the other
    three. Answered separately they can disagree: refuse the figure but
    keep the date, and the history shows a reading the asset does not have,
    on a day it was never taken.

    So a group is decided together and asked ONCE, by a single switch
    beside the figure -- the part of it a reader is actually weighing. The
    debt amount, its currency and its own series are the same
    relationship, asked beside the debt amount.

    The key of each group is the field that carries the switch; everything
    listed under it follows silently.
  */
  const DECISION_GROUPS = {
    // Asked at the date, which opens the reading row.
    evaluation_date: [
      "evaluation_date",
      "asset_value",
      "currency",
      "asset_evaluation_records",
    ],
    // And at the type, which opens the debt block.
    debt_type: [
      "debt_type",
      "debt_amount",
      "debt_currency",
      "debt_annual_interest",
      "debt_payoff_date",
      "asset_debt_records",
    ],
  };

  /*
    The members of a group that this proposal actually touches.

    A member counts when the partner proposed it OUTRIGHT, or when it can
    be read off a series they proposed -- a new reading carries its own
    date, value and currency, so proposing the series alone is a proposal
    about all three even though no scalar came with it. Without the second
    case the date stayed blank: nothing had been proposed under its name,
    so nothing was ever written into it.
  */
  const groupInPlay = (lead, fallback) => {
    const group = DECISION_GROUPS[lead] || [fallback];
    const members = group.filter(
      (name) =>
        resolvedProposals[name] ||
        (SERIES_OF[name] && resolvedProposals[SERIES_OF[name]])
    );
    return members.length ? members : [fallback];
  };

  // The field whose switch answers for this one.
  const DECISION_LEAD = Object.fromEntries(
    Object.entries(DECISION_GROUPS).flatMap(([lead, members]) =>
      members.map((member) => [member, lead])
    )
  );

  const decideProposal = (field, takeNew) => {
    /*
      The field, and its other half when that was proposed too -- a pair
      only exists when the partner changed both, which is what proposing a
      new valuation does.
    */
    const lead = DECISION_LEAD[field] || field;
    const fields = groupInPlay(lead, field);

    setDecisions((prev) => {
      const next = { ...prev };
      for (const name of fields) next[name] = takeNew;
      /*
        And under the LEAD's own name, even when the partner proposed
        nothing for it. ProposedField reads `decisions[field]` for the
        field it was given, so without this the switch beside the date has
        no state of its own: it draws as Previous and never moves, however
        many times it is pressed.
      */
      next[lead] = takeNew;
      return next;
    });

    /*
      Into whichever state actually owns each field.

      The evaluation panel keeps its own copy of the valuation and debt
      fields, and `mergeEvaluationIntoAsset` folds that copy over the asset
      on save -- so writing an accepted change into `asset` would have been
      silently overwritten by the panel a moment later, and the customer
      would have approved something that never landed. Ownership is read off
      the panel's own starting shape rather than a list kept in step by hand.
    */
    const onAsset = {};
    const onEvaluation = {};
    for (const name of fields) {
      /*
        A member that is only in play through its series has nothing of its
        own to accept if that series came through empty -- writing the
        `undefined` would blank a field the partner never asked about.
      */
      const accepted = acceptedValue(name);
      if (takeNew && accepted === undefined) continue;
      if (Object.prototype.hasOwnProperty.call(evaluationBaseline, name)) {
        onEvaluation[name] = takeNew
          ? accepted
          : previousFromAsset[name]
          ? previousFromAsset[name](toEdit)
          : evaluationBaseline[name];
      } else {
        onAsset[name] = takeNew ? accepted : toEdit?.[name];
      }
    }
    if (Object.keys(onEvaluation).length) {
      setAssetEvaluation((prev) => ({ ...prev, ...onEvaluation }));
    }
    if (Object.keys(onAsset).length) {
      setAsset((prev) => ({ ...prev, ...onAsset }));
    }
  };

  /*
    The readings a partner's proposal would ADD, for the history table to show
    marked.

    A records change is proposed whole -- that is how the field is stored --
    so what is new has to be worked out by comparing the two lists. Matched on
    id where there is one and on date+value otherwise, which is what the
    evaluation panel itself uses to tell two readings apart.

    Only while the suggestion is unanswered: once the customer takes it, the
    readings are in `asset` and the table shows them as its own.
  */
  // One frozen empty set, so an unchanged prop does not re-render the panel.
  const EMPTY_KEYS = React.useMemo(() => new Set(), []);
  const recordKey = (r) => String(r?.id ?? `${r?.date}|${r?.value}`);

  /*
    The rows a proposal would ADD, whatever the customer has decided about it.

    Separate from the block below because the two answer different questions:
    this one is "which rows are the suggestion", and it stays true after the
    suggestion is accepted -- which is exactly when the rows need marking,
    because accepting moves them into the asset's own list and they stop
    being distinguishable from readings the customer took themselves.
  */
  const proposedKeys = (field) => {
    const proposed = asList(proposals[field]?.value);
    if (!proposed.length) return EMPTY_KEYS;
    /*
      Keyed on id, or the date when a record predates ids -- NOT on the
      value. The table matches these against rows that have been through the
      display-currency conversion, which rewrites `value`; a key containing
      it would match only while the panel happened to be showing the
      currency the record was stored in. The date is exact on its own
      because a series holds one record per date (see upsertRecord).

      CHANGED rows count, not only added ones. A partner correcting the
      figure on a reading the customer already had proposes a record with
      that reading's own date, so "added" finds nothing and the row the
      customer is being asked about goes unmarked -- which is most of what
      this is for. The comparison is on the raw values, here, where nothing
      has been converted yet.
    */
    const markKey = (r) => String(r?.id ?? r?.date);
    const same = (a, b) =>
      String(a?.value) === String(b?.value) &&
      String(a?.currency || "") === String(b?.currency || "");
    const mine = new Map(asList(toEdit?.[field]).map((r) => [markKey(r), r]));
    const touched = proposed
      .filter((r) => {
        const theirs = mine.get(markKey(r));
        return !theirs || !same(theirs, r);
      })
      .map(markKey);
    return touched.length ? new Set(touched) : EMPTY_KEYS;
  };

  /*
    And the ones to draw as a separate marked block UNDER the asset's own.

    Only while the question is open. Once it is answered the rows belong to
    one side or the other: accepted, they are in the asset's list and are
    marked there; refused, they are nowhere, which is the point of refusing.

    Drawing them while REFUSED was the bug -- toggling to Previous left the
    suggested reading on screen, flagged, as though it were still going to
    be kept. Saving did discard it, so the table was contradicting what the
    save was about to do.
  */
  const addedRecords = (field) => {
    if (decisions[field] !== undefined) return [];
    const proposed = asList(proposals[field]?.value);
    if (!proposed.length) return [];
    const have = new Set(asList(toEdit?.[field]).map(recordKey));
    return proposed.filter((r) => !have.has(recordKey(r)));
  };

  const sections = editableSections(toEdit);
  const canPropose =
    readOnly &&
    Boolean(userData.partnerPropose) &&
    canEditAnything("asset", toEdit);
  /*
    Marks a subtree the partner may type in. An empty object on the customer's
    own modal, where nothing is locked in the first place.
  */
  const editableIf = (on) => (canPropose && on ? { "data-viewonly-editable": "" } : {});
  /*
    The documents section specifically, because its controls are buttons and
    drop zones rather than fields -- ViewOnly's `disabled` never reaches them,
    so each one is gated on this by hand.

    A partner adding a file here is proposing it: the file is uploaded (it has
    to exist before a list can name it) and the NEW LIST is what waits for the
    customer. Removing one proposes the shorter list and deletes nothing --
    the file stays until the customer accepts, because a refused proposal
    must leave the asset exactly as it was.
  */
  const canEditFiles = !readOnly || (canPropose && sections.files);
  /*
    Whose storage a file belongs in: the customer's when a partner is the one
    attaching it, their own otherwise. /file/upload writes into the session
    user's directory, which for a partner is one the customer cannot read.

    Keyed on partnerReadOnly, not readOnly -- that is true of the whole view,
    including a draft the partner may still edit.
  */
  const uploadTarget = userData.partnerReadOnly
    ? userData.UserData?.user_id
    : null;
  const approvalContent =
    websiteCurrentLang?.content?.User?.PartnerApproval?.PartnerApproval;
  /*
    The reading fields (date, value, currency) open empty — the panel is a form
    for the next evaluation, not a view of the last one. What describes the
    asset rather than a reading carries over: the financial exposure, is_liquid
    and the saved data entries.
  */
  /*
    Custom asset type. The modal always opens on the DROPDOWN, even when the
    saved type is one of the user's own: that type is offered in the list, so
    showing it selected there is both accurate and editable. The free-text field
    is only for entering a NEW name, reached by picking "Custom".
  */
  const [customType, setCustomType] = useState(false);
  const [customTypeLabel, setCustomTypeLabel] = useState("");

  /*
    What the evaluation panel opens with -- held, not just used once.

    Two jobs. It seeds the panel's state, and it is what declining a
    partner's suggestion about one of those fields restores. Those are not
    the same value as the asset's: the panel's reading fields (date, value,
    currency, debt amount) deliberately open BLANK, because they are a form
    for the next reading rather than a view of the last one. Putting
    `toEdit.asset_value` back into them would stage a re-record of a reading
    the customer never took.
  */
  const evaluationBaseline = useMemo(
    () =>
      evaluationFromAsset(
        toEdit,
        userData?.UserData?.financial_exposure_custom_options
      ) || EMPTY_EVALUATION,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toEdit?.asset_id]
  );
  const [assetEvaluation, setAssetEvaluation] = useState(evaluationBaseline);
  const [modal, setModal] = useState();
  /*
    Every suggestion arrives accepted.

    The alternative is a modal that opens showing the customer's own values
    with switches offering something they have to flip one at a time to even
    see. Starting from the partner's version means the asset reads as it
    WOULD read if they saved, and refusing one is a single flip back -- which
    is also what makes saving an answer to all of them rather than only to
    the ones that were touched.

    Once per record, and never on the partner's own screen: they are the ones
    asking, and pre-filling their form with their own pending proposal would
    have them re-propose it.
  */
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (defaultsApplied.current || readOnly) return;
    const proposed = Object.keys(resolvedProposals);
    if (!proposed.length) return;
    defaultsApplied.current = true;
    /*
      Expanded the same way a click expands: every field a proposal touches,
      plus the lead whose switch has to show the state. See groupInPlay.
    */
    const names = new Set(proposed);
    for (const name of proposed) {
      const lead = DECISION_LEAD[name];
      if (!lead) continue;
      names.add(lead);
      for (const member of groupInPlay(lead, name)) names.add(member);
    }
    const fields = [...names];
    setDecisions(Object.fromEntries(fields.map((field) => [field, true])));
    /*
      Into whichever state owns each field -- the same split decideProposal
      makes, for the same reason: the evaluation panel keeps its own copy and
      folds it over the asset on save.
    */
    const onAsset = {};
    const onEvaluation = {};
    for (const field of fields) {
      const value = acceptedValue(field);
      // A lead with nothing of its own to carry contributes no value.
      if (value === undefined) continue;
      if (Object.prototype.hasOwnProperty.call(evaluationBaseline, field)) {
        onEvaluation[field] = value;
      } else {
        onAsset[field] = value;
      }
    }
    if (Object.keys(onAsset).length) {
      setAsset((prev) => ({ ...prev, ...onAsset }));
    }
    if (Object.keys(onEvaluation).length) {
      setAssetEvaluation((prev) => ({ ...prev, ...onEvaluation }));
    }
    /*
      `acceptedValue` is rebuilt every render and is deliberately not a
      dependency: this pass runs once per record (defaultsApplied) and
      listing it would only re-run an effect that immediately returns.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProposals, readOnly, evaluationBaseline]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [asset, setAsset] = useState({
    asset_id: toEdit?.asset_id,
    asset_name: toEdit?.asset_name,
    asset_type: toEdit?.asset_type,
    description: toEdit?.description,
    credentials: toEdit?.credentials,
    /*
      Coerced to arrays. decryptAsset guarantees this now, but the modal is the
      thing that crashes when it is wrong (`beneficiaries.map is not a
      function`), so it does not take the type on trust.
    */
    beneficiaries: Array.isArray(toEdit?.beneficiaries)
      ? toEdit.beneficiaries
      : [],
    trustees: Array.isArray(toEdit?.trustees) ? toEdit.trustees : [],
    asset_files: Array.isArray(toEdit?.asset_files) ? toEdit.asset_files : [],
    deleted_files: [],
    /*
      Carried through for the same reason the evaluation records are: this
      object is what the save writes back, so a field left out is dropped.
      pending_approval is not here because the save sets it deliberately --
      saving is approving -- but who created the asset outlives that.
    */
    created_by_partner: toEdit?.created_by_partner || "",
    /*
      The partner's outstanding suggestions, carried for the same reason:
      this object is what the save writes back. The save empties the list --
      saving is the answer to every one of them -- but it has to hold the
      list in order to clear it, and a partner's save reads it to know what
      is already queued.
    */
    partner_changes: Array.isArray(toEdit?.partner_changes)
      ? toEdit.partner_changes
      : [],
    /*
      Asset Evaluation. This object is what the save writes back, so anything
      omitted here is silently dropped — hence carrying the records and data
      entries through even though the panel below owns the editable copy.
    */
    financial_exposure: toEdit?.financial_exposure || "",
    evaluation_date: toEdit?.evaluation_date || "",
    asset_value: toEdit?.asset_value ?? "",
    currency: toEdit?.currency || "",
    is_liquid: toEdit?.is_liquid === true,
    has_associated_debt: toEdit?.has_associated_debt === true,
    debt_type: toEdit?.debt_type || "",
    debt_amount: toEdit?.debt_amount ?? "",
    debt_currency: toEdit?.debt_currency || "",
    debt_annual_interest: toEdit?.debt_annual_interest ?? "",
    debt_payoff_date: toEdit?.debt_payoff_date || "",
    asset_evaluation_records: Array.isArray(toEdit?.asset_evaluation_records)
      ? toEdit.asset_evaluation_records
      : [],
    asset_debt_records: Array.isArray(toEdit?.asset_debt_records)
      ? toEdit.asset_debt_records
      : [],
    data_entries: Array.isArray(toEdit?.data_entries) ? toEdit.data_entries : [],
  });
  const [error, setError] = useState({
    asset_name: "",
    asset_type: "",
    description: "",
  });
  const [formErrors, setFormError] = useState({
    service_name: "",
    username: "",
    password: "",
    record_name: "",
  });

  const [duplicatedName, setDuplicatedName] = useState(false);
  const duplicateError = "You already have an asset with this name";

  const [files, setFiles] = useState([]);
  const [needToClear, setNeedToClear] = useState(false);

  const handleFile = (e) => {
    const file_array = Array.from(e.target.files);

    // show error if one of loaded files is more than 100mb
    const sizeError = isValidFileSize(file_array);
    if (sizeError) {
      setError({
        file_size: parse(sizeError),
      });
      setTimeout(() => {
        setError({
          file_size: "",
        });
      }, 10000);
      setNeedToClear(true);
      return;
    }

    const filesFreeSlots = maxFilesToUpload - asset.asset_files.length;
    if (
      file_array.length > maxFilesToUpload ||
      filesFreeSlots < file_array.length
    ) {
      setError({
        files: `You are trying to upload too many files. The maximum allowed is ${maxFilesToUpload} files.`,
      });
      setTimeout(() => {
        setError({
          files: "",
        });
      }, 5000);
      return;
    }
    let asset_files = [];
    file_array.map((newFile) => {
      console.log(newFile);
      let asset_file = {
        id: RandomId(32),
        name: newFile.name,
        size: newFile.size,
        lastModified: newFile.lastModified,
      };
      asset_files.push(asset_file);
      let newBlob = new Blob([newFile], {
        type: newFile.type,
        lastModified: newFile.lastModified,
      });
      let renamedFile = new File([newBlob], asset_file.id, {
        type: newFile.type,
      });
      setFiles((files) => [...files, renamedFile]);
      return null;
    });
    setAsset({ ...asset, asset_files: asset.asset_files.concat(asset_files) });
  };
  const maxFilesToUpload = "8";
  // textarea
  const maxMessageLength = 10000; // Define the maximum character length for the message field

  const textareaRef = useRef(null);

  const handleChangeDescription = () => {
    const textarea = textareaRef.current;
    // Reset the textarea height to 0 to correctly calculate the scrollHeight
    textarea.style.height = "0";
    const height = textarea.scrollHeight;
    textarea.style.height = `${height}px`;
  };
  // textarea

  useEffect(() => {
    console.log(asset);
    console.log(files);
  }, [asset, files]);
  const deleteFile = (fileO) => {
    console.log(fileO);
    const new_files = files.filter((file) => file.name !== fileO.id);
    setFiles(new_files);
    setAsset({
      ...asset,
      asset_files: asset.asset_files.filter((file) => file.id !== fileO.id),
      deleted_files: (toEdit.asset_files || []).some(
        (file) => file.id === fileO.id
      )
        ? [...asset.deleted_files, fileO.id]
        : asset.deleted_files,
    });
    // deleted_files.push(fileO.id)
    // console.log(deleted_files)
  };
  const deleteAsset = async () => {
    onClose();
    floatMessage(dublicatedcontent?.deleted);
    asset.asset_files.map((asset_file) => {
      DeleteFile(asset_file.id)
        .then(() => {
          console.log(
            `Successfully deleted ${asset.asset_name}:${asset_file.id}`
          );
        })
        .catch((e) => {
          console.log(
            `Error while deleting ${asset.asset_name}:${asset_file.id}`
          );
        });
      return null;
    });
    /*
      ---- the beneficiaries this asset was attached to ----

      The link is dropped from each of them, and so is any partner proposal
      that existed only to make it.

      Refusing a partner's asset is also refusing the attachment they
      proposed for it: there is nothing left to attach. Left queued, the
      beneficiary kept its "suggested changes" mark and the customer opened
      it to find a proposal that, with the asset gone, asked for nothing --
      and accepting it would have written back an id pointing at an asset
      that no longer exists.

      Only a proposal that has become a NO-OP is dropped. One that still says
      something -- the partner moved two assets and the customer refused one
      -- is left for them to answer, minus the id that went with the asset.
      That is what comparing the merged result against the list decides;
      mergeProposedIds is the same merge the beneficiary modal accepts
      through, so the question asked here is exactly "would accepting this
      still change anything".
    */
    const survives = (id) => id !== asset.asset_id;
    const partnerCouldSee = (id) =>
      userData.UserData.assets.find((a) => a.asset_id === id)
        ?.share_with_partner === true;
    const sameIds = (a, b) => {
      if (a.length !== b.length) return false;
      const left = new Set(a.map(String));
      return b.every((id) => left.has(String(id)));
    };

    var new_beneficiaries = await Promise.all(
      userData.UserData.beneficiaries.map(async (beneficiary) => {
        const visible_assets = (beneficiary.visible_assets || []).filter(
          survives
        );
        const changes = Array.isArray(beneficiary.partner_changes)
          ? beneficiary.partner_changes
          : [];
        if (!changes.some((change) => change?.field === "visible_assets")) {
          return { ...beneficiary, visible_assets };
        }
        /*
          Reading it needs the customer's key, which is why this is the only
          async step here: the value was encrypted for them by the partner.
          A proposal that cannot be read is left exactly as it was.
        */
        let proposed = null;
        try {
          const read = await readProposals(beneficiary, userData.private_key);
          const value = read?.visible_assets?.value;
          if (Array.isArray(value)) proposed = value.filter(survives);
        } catch (e) {
          proposed = null;
        }
        if (proposed === null) return { ...beneficiary, visible_assets };

        const merged = mergeProposedIds(
          visible_assets,
          proposed,
          partnerCouldSee
        );
        return {
          ...beneficiary,
          visible_assets,
          partner_changes: sameIds(merged, visible_assets)
            ? changes.filter((change) => change?.field !== "visible_assets")
            : changes,
        };
      })
    );
    var new_issues = handleIssues({
      ...userData.UserData,
      assets: userData.UserData.assets.filter(
        (asset_) => asset_.asset_id !== asset.asset_id
      ),
      beneficiaries: new_beneficiaries,
    });
    userData.setUserData({
      ...userData.UserData,
      assets: userData.UserData.assets.filter(
        (asset_) => asset_.asset_id !== asset.asset_id
      ),
      beneficiaries: new_beneficiaries,
      issues: new_issues,
    });
    userData.Update(RandomId(10));
    userData.setCustomerUser({
      ...userData.CustomerUser,
      user_status: userData.UserData.assets
        .filter((asset_) => asset_.asset_id !== asset.asset_id)
        .some((e) => e.beneficiaries.length > 0)
        ? "Active"
        : "Inactive",
    });
  };
  const checkEmptyFields = () => {
    var isError = false;
    var error_aux = {
      asset_name: "",
      asset_type: "",
      description: "",
      files: "",
    };
    if (asset.asset_name === "") {
      error_aux.asset_name = "Please set an asset name";
      isError = true;
    }

    /*
      "Custom" is a prompt for a name, not a type. Unnamed it resolves to no
      type at all, and the save then quietly fell back to whichever type was
      selected before it — no error, the modal closed, and the asset was stored
      as something the user never chose.
    */
    if (customType && customTypeLabel.trim() === "") {
      error_aux.asset_type =
        content?.addAssetError7 || "Please name the custom asset type";
      isError = true;
    }

    if (asset.asset_name !== "") {
      // check if asset with this name is exist
      const editedAssetName = asset.asset_name;
      const editedAssetId = asset.asset_id;
      let hasMatchingAsset = userData.UserData.assets.some(
        (assetItem) =>
          assetItem.asset_name.toLowerCase() ===
            editedAssetName.toLowerCase() &&
          assetItem.asset_id !== editedAssetId
      );

      if (hasMatchingAsset) {
        isError = true;
        setDuplicatedName(true);
        document.getElementById("scrollContainer").scrollTop = 0;
      }
    }

    if (asset.description.length > maxMessageLength) {
      error_aux.description =
        "The description is too big. Please reduce the number of characters";
      isError = true;
    }
    if (asset.asset_files.length > maxFilesToUpload) {
      error_aux.files =
        "There are too many files. Please delete unnecessary files";
      isError = true;
    }
    // if (asset.description === "" && asset.asset_files.length === 0) {
    //   error_aux.description = "Please set a description or upload a file";
    //   isError = true;
    // }
    if (asset.description === "" && asset.asset_files.length === 0) {
      const showNotificationMessage =
        sessionStorage.getItem("show_notification");
      if (!showNotificationMessage) {
        // setOpenModal(true);
      }
      //   error_aux.description = content.addAssetError6;
      //   isError = true;
    }
    /*
      A partner proposes nothing here, so there is nothing to check.

      They are never sent credentials and cannot be: the section is hidden
      for them, the field is empty, and `credentials` is on no whitelist. Run
      against that empty value the check below would have failed the save for
      a field they were never shown.
    */
    const aux_credentials = canPropose
      ? {}
      : parseCredentials(asset.credentials);
    if (
      Object.keys(aux_credentials).some(
        (key) => key !== "mfa" && aux_credentials[key] === ""
      )
    ) {
      isError = true;
    }

    const newFormErrors = {
      service_name: "",
      username: "",
      password: "",
      record_name: "",
    };

    for (const field in aux_credentials) {
      if (aux_credentials[field] === "" && field !== "mfa") {
        newFormErrors[field] = "error";
      }
    }

    setFormError(newFormErrors);
    setError(error_aux);
    setTimeout(() => {
      setError({
        asset_name: "",
        asset_type: "",
        description: "",
        files: "",
        credentials: "",
      });
    }, 5000);
    return isError;
  };
  const editAsset = (key, value) => {
    setAsset((prevData) => {
      return { ...prevData, [key]: value };
    });
  };

  /*
    Deleting a history row removes it immediately rather than on Save, which is
    what the delete control implies. There is no per-record endpoint: the record
    is filtered out of the array and pushed through setUserData, which is the
    same path an asset deletion already takes (see deleteAsset above).

    Records are matched by id — the row index cannot be trusted, because the
    tables render values converted into the display currency.
  */
  const deleteRecord = (collection, record) => {
    if (!record?.id) return;
    const remaining = (asset?.[collection] || []).filter(
      (entry) => entry.id !== record.id
    );
    setAsset((prevData) => ({ ...prevData, [collection]: remaining }));
    userData.setUserData({
      ...userData.UserData,
      assets: userData.UserData.assets.map((asset_) =>
        asset_.asset_id === asset.asset_id
          ? { ...asset_, [collection]: remaining }
          : asset_
      ),
    });
    userData.Update(RandomId(10));
  };
  /*
    The Save under the currency in the evaluation panel: record this reading
    now, without saving the rest of the asset and without closing the modal.

    Written straight through to the database, exactly as deleting a record above
    is. The asset already exists, so a reading taken against it is a fact about
    it — and the user was shown a Save and told it saved. Leaving it in the form
    to be committed later would mean closing the modal silently discarded
    something the button said it had kept.

    Only the reading is written. mergeReadingIntoAsset touches the exposure, the
    date, the value, the currency and the evaluation series; the debt block and
    the ownership rows are not folded in, because this button never checked
    them and a half-typed debt must not be saved on the back of a valuation.
  */
  const saveReading = (evaluation) => {
    const { asset: withReading, exposureOptions } = mergeReadingIntoAsset(
      asset,
      evaluation,
      userData.UserData?.financial_exposure_custom_options
    );
    setAsset(withReading);
    userData.setUserData({
      ...userData.UserData,
      assets: userData.UserData.assets.map((asset_) =>
        asset_.asset_id === withReading.asset_id ? withReading : asset_
      ),
      // A newly typed custom exposure belongs to the user, not to the asset.
      financial_exposure_custom_options: exposureOptions,
    });
    userData.Update(RandomId(10));
    floatMessage(dublicatedcontent?.saved);
  };


  /*
    The Save inside the debt block: record the debt now, and nothing else.

    The mirror of saveReading above, and like it a shortcut rather than a gate.
    The debt is written either way -- the asset's own Save folds the panel's
    figures in as it folds in everything else. This button only brings that
    forward, committing the debt without saving or closing the asset.

    Written straight through, as the reading is: the user was shown a Save and
    told it saved, so leaving it in the form to be committed later would mean
    closing the modal silently discarded it.

    What the asset's Save will NOT do is demand these fields. A half-filled
    debt must not block a save that had nothing to do with it, which is why
    the checks below live here and not in validate().
  */
  const saveDebt = (evaluation) => {
    const withDebt = mergeDebtIntoAsset(asset, evaluation);
    setAsset(withDebt);
    userData.setUserData({
      ...userData.UserData,
      assets: userData.UserData.assets.map((asset_) =>
        asset_.asset_id === withDebt.asset_id ? withDebt : asset_
      ),
    });
    userData.Update(RandomId(10));
    floatMessage(dublicatedcontent?.saved);
  };

  /*
    The Save inside one "New Field": record that one row, and nothing else.

    Same contract as the two above, narrowed to a single entry. Each new field
    is its own little form -- an ownership split, a note, a document reference
    -- and the user fills them one at a time, so each gets its own button
    rather than being held hostage by whichever other row is half-finished.

    Only the entry identified by `entryId` is folded in. The other entries, the
    reading and the debt are left exactly as the asset already holds them.
  */
  const saveEntry = (evaluation, entryId) => {
    const { asset: withEntry, ownerOptions } = mergeEntryIntoAsset(
      asset,
      evaluation,
      entryId,
      userData.UserData?.asset_owner_custom_options,
      ownerOptionsFrom(userData.UserData || {}, asset?.asset_id)
    );
    setAsset(withEntry);
    userData.setUserData({
      ...userData.UserData,
      assets: userData.UserData.assets.map((asset_) =>
        asset_.asset_id === withEntry.asset_id ? withEntry : asset_
      ),
      // A newly typed owner belongs to the user, not to the asset.
      asset_owner_custom_options: ownerOptions,
    });
    userData.Update(RandomId(10));
    floatMessage(dublicatedcontent?.saved);
  };

  const trimNameField = (value) => {
    const name = value.trim();
    editAsset("asset_name", name);
  };
  const Progress = (status) => {
    setModal(
      <OnProgress title={dublicatedcontent?.work_in_progress} status={status} />
    );
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (checkEmptyFields()) {
      return;
    }

    /*
      Fold the Asset Evaluation panel back into the asset. This also appends a
      new evaluation/debt record when the date or value changed — an unchanged
      re-save appends nothing, which matters because DataContext writes on every
      state change, not only when Save is pressed.
    */
    /*
      Resolve the asset type first: a typed custom name becomes one of this
      user's own types, or selects an existing one rather than duplicating it.
    */
    const { asset_type: resolvedType, options: assetTypeOptions } =
      resolveAssetTypeSelection(
        customType ? ASSET_TYPE_CUSTOM : asset.asset_type,
        customTypeLabel,
        userData.UserData?.asset_type_custom_options
      );

    /*
      Owners typed into the "Custom" field become names on the asset and join
      this user's saved owner list, so the next asset offers them.
    */
    const {
      asset: evaluatedAsset,
      exposureOptions,
      ownerOptions,
    } = mergeEvaluationIntoAsset(
      // The sharing switches ride along on the asset; they are stored unencrypted.
      {
        ...asset,
        ...partnerSharing,
        asset_type: resolvedType || asset.asset_type,
      },
      /*
        The debt rides along, as everything else in the panel does.

        Its own Save is a shortcut, not a gate: it commits the debt on the
        spot, without saving or closing the asset. Not pressing it does not
        throw the figures away -- typing them and saving the asset saves them
        too. What the block's Save changes is WHEN, not WHETHER.

        The one thing the asset's Save will not do is DEMAND them: a debt
        half-filled, or a toggle ticked over empty fields, must not block a
        save that had nothing to do with the debt. That is why validate()
        checks the debt no longer -- see the note there.

        Saving twice over does not double anything. Debt records are dated the
        day they are inserted and upserted on that date, so the asset's Save
        replaces the record the block's Save just wrote.
      */
      assetEvaluation,
      userData.UserData?.financial_exposure_custom_options,
      userData.UserData?.asset_owner_custom_options,
      ownerOptionsFrom(userData.UserData || {}, asset?.asset_id)
    );

    /*
      A partner's save is a proposal, and stops here.

      `evaluatedAsset` is the asset as this partner would have it -- the same
      object the customer's save would write -- so the change list is the
      difference between that and what the customer actually has. Only fields
      this partner was granted are compared; the rest cannot differ, because
      the controls behind them were never enabled.

      Nothing below this runs for them: no setUserData, no Update, no file
      deletions, no beneficiary rewiring. The customer's record is untouched
      until the customer themselves accepts the change.
    */
    if (canPropose) {
      const fields = changedFields("asset", toEdit, evaluatedAsset);
      if (!fields.length) {
        floatMessage(
          approvalContent?.partnerApprovalText24 || "No changes to propose"
        );
        onClose();
        return;
      }
      /*
        Uploaded first: the proposed list names them, and a name pointing at
        nothing would be accepted by the customer and then fail to open.
        Nothing is deleted here -- see canEditFiles.
      */
      if (fields.includes("asset_files") && files.length) {
        await UploadFiles(
          files,
          userData.private_key,
          Progress,
          dublicatedcontent,
          uploadTarget
        );
      }
      /*
        Which files the proposal names, in plaintext, so the server knows
        they are spoken for -- see proposeChanges. Without this the orphan
        sweeper deletes them the moment the customer next loads their data.
      */
      const named = {};
      if (fields.includes("asset_files")) {
        named.asset_files = (evaluatedAsset.asset_files || [])
          .map((file) => file?.id)
          .filter(Boolean);
      }
      const result = await userData.partnerPropose(
        "asset",
        toEdit.asset_id,
        fields,
        evaluatedAsset,
        named
      );
      floatMessage(
        result?.ok
          ? approvalContent?.partnerApprovalText25 ||
              "Sent to your customer for approval"
          : approvalContent?.partnerApprovalText26 ||
              "Could not send those changes",
        result?.ok ? "success" : "error"
      );
      if (result?.ok) onClose();
      return;
    }

    var new_assets = userData.UserData.assets.map((asset_) => {
      if (asset_.asset_id === evaluatedAsset.asset_id) {
        /*
          Saving a partner-created asset is the approval, and the only thing
          that clears the flag. Until this runs, the asset is in no total and no
          beneficiary inherits it. created_by_partner is left alone: who created
          it stays true after it is accepted.
        */
        return {
          ...evaluatedAsset,
        /*
          And the partner's suggestions, settled -- all of them.

          Saving IS the answer. Every suggestion arrives with its switch
          on, so a value the customer left alone is one they kept and a
          switch they turned off is one they refused; either way the
          question has been put and answered, and none of them comes back.
          Leaving the untouched ones queued would ask again about changes
          that are already in the record.
        */
          partner_changes: [],
          pending_approval: false,
        };
      } else {
        return asset_;
      }
    });
    var new_beneficiaries = userData.UserData.beneficiaries.map(
      (beneficiary) => {
        if (asset.beneficiaries.includes(beneficiary.user_id)) {
          // The beneficiary should have this asset as visible asset
          return {
            ...beneficiary,
            visible_assets: [
              ...new Set([...beneficiary.visible_assets, asset.asset_id]),
            ],
          };
        } else {
          return {
            ...beneficiary,
            visible_assets: beneficiary.visible_assets.filter(
              (e) => e !== asset.asset_id
            ),
          };
        }
      }
    );
    asset?.deleted_files?.map((file_id) => {
      DeleteFile(file_id);
      return null;
    });

    UploadFiles(files, userData.private_key, Progress, dublicatedcontent)
      .then(() => {
        Progress("Updating data ...");
        var new_issues = handleIssues({
          ...userData.UserData,
          beneficiaries: new_beneficiaries,
          assets: new_assets,
        });
        userData.setUserData({
          ...userData.UserData,
          beneficiaries: new_beneficiaries,
          assets: new_assets,
          issues: new_issues,
          // A newly typed custom exposure type belongs to the user, not the asset.
          financial_exposure_custom_options: exposureOptions,
          asset_type_custom_options: assetTypeOptions,
          asset_owner_custom_options: ownerOptions,
        });
        userData.setCustomerUser({
          ...userData.CustomerUser,
          user_status: new_assets.some((e) => e.beneficiaries.length > 0)
            ? "Active"
            : "Inactive",
        });
        userData.Update(RandomId(10));
        setModal(null);
        onClose();
        floatMessage(dublicatedcontent?.saved);
      })
      .catch(() => {
        Progress("Something went wrong...");
        setTimeout(() => {
          setModal(null);
          onClose();
        }, 1000);
      });
  };
  /*
    Run through a catch rather than fired and forgotten.

    These savers are async, so anything they throw becomes a rejected promise
    nobody is holding: the button does nothing, there is no message, and
    nothing on screen says which line gave up. That is how a JSON.parse of an
    empty string hid through a whole round of testing. Logging it does not
    fix a failure, but it stops one being silent.
  */
  const runSave = (e) => {
    handleUpload(e).catch((error) => console.log(error));
  };
  const checkNotification = (e) => {
    // Asset Evaluation ownership must total 100%; scrolls itself into view.
    if (assetEvaluationRef.current && !assetEvaluationRef.current.validate()) {
      return;
    }
    if (asset.description === "" && asset.asset_files.length === 0) {
      const showNotificationMessage =
        sessionStorage.getItem("show_notification");
      if (!showNotificationMessage) {
        setOpenModal(true);
        return;
      }
    }
    runSave(e);
  };
  // Counted, so the <OnProgress> this modal renders on save does not unlock
  // the page when it closes -- see Utils/useScrollLock.
  useScrollLock();
  // Grow and shrink smoothly when a section inside opens or closes.
  useSmoothHeight(boxRef, divElementRef);

  useEffect(() => {
    if (openAssets) {
      const element = document.getElementById("scrollContainer");
      element.scrollTop = element.scrollHeight;
    }
  }, [openAssets]);

  useEffect(() => {
    if (openBeneficiary) {
      const element = document.getElementById("scrollContainer");
      element.scrollTop = element.scrollHeight;
    }
  }, [openBeneficiary]);

  const clearNameError = () => {
    setDuplicatedName(false);
  };

  return (
    <div className={styles.background}>
      <Modal
        footer={[
          <div className={styles.notificationModal_footer}>
            <Checkbox
              style={{
                textAlign: "left",
              }}
              onClick={(e) => {
                sessionStorage.setItem("show_notification", e.target.checked);
              }}
            >
              {content.editAssetText34}
            </Checkbox>

            <div className={styles.notificationModal_footer_buttons}>
              <Button
                onClick={(e) => {
                  setOpenModal(false);
                }}
                type="primary"
              >
                {dublicatedcontent.dublicatedText71}
              </Button>
              <Button onClick={(e) => runSave(e)}>
                {content.editAssetText36}
              </Button>
            </div>
          </div>,
        ]}
        onCancel={(e) => setOpenModal(false)}
        open={openModal}
        className={styles.notificationModal}
      >
        <div className={styles.notificationModal_container}>
          <img src={missingFilesIcon} alt="missingFiles" />
          <h2>{content.editAssetText35}</h2>
        </div>
        <p>{parse(content.editAssetText33)}</p>
      </Modal>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={
          width <= 599
            ? {
                height: window.innerHeight - 10,
              }
            : {}
        }
        ref={boxRef}
        className={styles.main_box}
      >
        <div onClick={onClose} className={styles.close_logo}></div>
        {/* Read-only for a partner: see canEditRecord. */}
        <ViewOnly active={readOnly}>
        <div className={styles.title}>
          {content.editAssetText3} {asset.asset_name}
        </div>
        <div
          /* Scrolls even when the contents are inert. */
          data-viewonly-scroll
          className={`${styles.popup_scrollable} ${styles.popup_scrollable_edit}`}
          id="scrollContainer"
          ref={divElementRef}
        >
          <div
            className={`${styles.shadow_top} ${
              showTopShadow ? styles.active : ""
            }`}
          />

          {/*
            Above the asset's own fields: what is being asked has to be read
            before the fields below it mean anything.
          */}
          <div className={styles.main_data} {...editableIf(sections.description)}>
            <div className={styles.main_data_upper}>
              <div className={styles.upper_left}>
                {review(
                  "asset_name",
                  dublicatedcontent?.dublicatedText63,
                  <div
                    className={`${styles.input_title} ${styles.required_field}`}
                  >
                    {dublicatedcontent.dublicatedText63}
                  </div>
                )}
                <input
                  disabled={userData.isFreezed}
                  onBlur={(e) => trimNameField(e.target.value)}
                  value={asset.asset_name}
                  onClick={(e) => clearNameError()}
                  onChange={(e) => editAsset("asset_name", e.target.value)}
                  className={`${styles.beneficiary_input} ${
                    error.asset_name ? styles.field_error : ""
                  } ${duplicatedName ? styles.field_error : ""} ${reviewMark(
                    "asset_name"
                  )}`}
                  placeholder={dublicatedcontent.dublicatedText63}
                ></input>
                {duplicatedName ? (
                  <div className={styles.error_message}>{duplicateError}</div>
                ) : null}
                <div className={styles.error_message}>
                  {error.asset_name === "" ? "" : error.asset_name}
                </div>
              </div>

              <div className={styles.upper_right}>
                {review(
                  "asset_type",
                  dublicatedcontent?.dublicatedText58,
                  <div className={styles.delay_period}>
                    <div
                      className={`${styles.input_title} ${styles.required_field}`}
                    >
                      {dublicatedcontent.dublicatedText58}
                    </div>
                  </div>
                )}
                {customType ? (
                  <div className={styles.custom_type_wrap}>
                    <input
                      autoFocus
                      type="text"
                      className={`${styles.select} ${
                        error.asset_type ? styles.field_error : ""
                      } ${reviewMark("asset_type")}`}
                      disabled={userData.isFreezed}
                      placeholder={
                        dublicatedcontent?.enter_a_name || "Enter a name"
                      }
                      value={customTypeLabel}
                      onChange={(e) => {
                        // Clear as soon as it has a name.
                        if (e.target.value.trim() && error.asset_type) {
                          setError((prev) => ({ ...prev, asset_type: "" }));
                        }
                        setCustomTypeLabel(e.target.value);
                      }}
                    />
                    {/* Back to the dropdown, discarding what was typed. */}
                    <button
                      type="button"
                      className={styles.custom_type_clear}
                      aria-label={dublicatedcontent?.cancel || "Cancel"}
                      title={dublicatedcontent?.cancel || "Cancel"}
                      onClick={() => {
                        setCustomType(false);
                        setCustomTypeLabel("");
                        setError((prev) => ({ ...prev, asset_type: "" }));
                      }}
                    />
                    <div className={styles.error_message}>
                      {error.asset_type}
                    </div>
                  </div>
                ) : (
                <Dropdown
                  disabled={userData.isFreezed}
                  /*
                    Canonicalised: an asset saved under a legacy spelling — the
                    old "online_storage" — has no matching option, so the
                    control would render blank and look as though the type had
                    been lost.
                  */
                  value={canonicalAssetType(asset.asset_type)}
                  onChange={(next) => {
                    if (next === ASSET_TYPE_CUSTOM) {
                      setCustomType(true);
                      setCustomTypeLabel("");
                      return;
                    }
                    editAsset("asset_type", next);
                  }}
                  groups={assetTypeGroupsFor({
                    currentValue: asset.asset_type,
                    customOptions: userData.UserData?.asset_type_custom_options,
                    translate: (value) =>
                      getAssetTypeTranslated(value, content, dublicatedcontent),
                    customLabel: dublicatedcontent?.custom || "Custom",
                  })}
                  placeholder={dublicatedcontent.dublicatedText58}
                  triggerClassName={`${styles.select} ${
                    styles.type_trigger
                  } ${reviewMark("asset_type")}`}
                  maxMenuHeight={ASSET_TYPE_MENU_HEIGHT}
                  ariaLabel={dublicatedcontent.dublicatedText58}
                  title={getAssetTypeTranslated(
                    canonicalAssetType(asset.asset_type),
                    content,
                    dublicatedcontent
                  )}
                />
                )}
              </div>
            </div>
            <div className={styles.main_data_lower}>
              {review(
                "description",
                dublicatedcontent?.dublicatedText59,
                <div className={styles.input_title}>
                  {dublicatedcontent.dublicatedText59}
                </div>
              )}
              <textarea
                disabled={userData.isFreezed}
                placeholder={dublicatedcontent.dublicatedText59}
                maxlength={maxMessageLength}
                value={asset.description}
                ref={textareaRef}
                onChange={(e) => {
                  editAsset("description", e.target.value);
                  handleChangeDescription(e);
                }}
                className={`${styles.large_message} ${
                  error.description ? styles.field_error : ""
                } ${reviewMark("description")}`}
              ></textarea>
              <div
                className={`${styles.counter} ${
                  asset.description.length === maxMessageLength
                    ? styles.error_text
                    : ""
                }`}
              >
                {asset.description.length === maxMessageLength ? (
                  <>{content.editAssetText19}| </>
                ) : (
                  ""
                )}
                {asset.description.length}/{maxMessageLength}{" "}
                {dublicatedcontent.dublicatedText93}
              </div>
              <div className={styles.error_message}>
                {error.description === "" ? "" : error.description}
              </div>
            </div>
          </div>
          {/*
            Hidden rather than unmounted only to keep this deep block from
            being reindented; `.upload_doc` sets no display, so `hidden` takes.

            A read-only viewer sees this section only when there is something
            in it. Empty, it reads as "this asset has no documents" -- which is
            not what an absent list means: the customer may have documents and
            simply not have shared them. Same reason is_liquid is withheld
            rather than shown as "No".
          */}
          <div
            className={styles.upload_doc}
            hidden={readOnly && !canEditFiles && asset.asset_files.length === 0}
            {...editableIf(sections.files)}
          >
            {review(
              "asset_files",
              content?.editAssetText20,
              <div className={styles.delay_period} style={{ marginTop: "20px" }}>
                <div className={styles.bold_title}>
                  {content.editAssetText20}
                </div>
                <Info message={content.editAssetText21} />
              </div>
            )}
            {canEditFiles && (
              <div
                className={styles.input_title}
                style={{ letterSpacing: "-0.02em" }}
              >
                {content.editAssetText22}
              </div>
            )}
            {/*
              Opening a shared file is reading, which ViewOnly allows -- without
              the exemption its inherited pointer-events:none swallows the click
              and the tiles look enabled but do nothing. Safe to open up wholesale:
              the delete affordance and the upload control are both gone below
              when readOnly, so a tile is all that is left in here.
            */}
            <div
              className={`${styles.file_container} ${reviewMark(
                "asset_files",
                "block"
              )}`}
              data-viewonly-allow
            >
              {asset?.asset_files?.map((file) => {
                return (
                  <FileIcon
                    deleteable={!userData.isFreezed && canEditFiles}
                    key={file.id}
                    OnClick={() =>
                      files.some((file_) => file_.name === file?.id)
                        ? downloadLocalFile(
                            files.filter((file_) => file_.name === file?.id)[0],
                            file.name
                          )
                        : downloadFile(
                            file.id,
                            file.name,
                            userData.private_key,
                            Progress,
                            dublicatedcontent,
                            /*
                              Partner view: the bytes are in the CUSTOMER's
                              folder, so the request has to say whose. Keyed on
                              partnerReadOnly, not readOnly -- that is true of
                              the whole view, including a draft the partner may
                              still edit.
                            */
                            userData.partnerReadOnly
                              ? userData.UserData?.user_id
                              : null
                          )
                            .then(() => setModal(null))
                            .catch((e) => {
                              Progress(`${e} occurred... Try later`);
                              setTimeout(() => {
                                setModal(null);
                              }, 2000);
                            })
                    }
                    file_id={file.id}
                    filename={file.name}
                    date={file.lastModified}
                    size={file.size}
                    OnDelete={() => {
                      console.log("File to be deleted:");
                      console.log(file);
                      deleteFile(file);
                    }}
                  ></FileIcon>
                );
              })}
              {asset.asset_files.length <= maxFilesToUpload - 1 && canEditFiles && (
                <Upload
                  disabled={userData.isFreezed}
                  handleFile={(e) => handleFile(e)}
                  clearField={needToClear}
                />
              )}
            </div>
            <div className={styles.error_message}>
              {error.files === "" ? "" : error.files}
            </div>
            {canEditFiles && (
              <div className={styles.file_counter}>
                {content.editAssetText23}
                <div
                  className={`${styles.file_counter} ${
                    asset.asset_files.length >= maxFilesToUpload
                      ? styles.error_text
                      : ""
                  }`}
                >
                  {asset.asset_files.length}/{maxFilesToUpload}{" "}
                  {dublicatedcontent?.dublicatedText94}
                </div>
              </div>
            )}
            {error.file_size ? (
              <div className={`${styles.error_message} ${styles.file_error}`}>
                {error.file_size}
              </div>
            ) : null}
          </div>
          {/*
            `display: contents` so the marker adds no box to a panel whose
            layout is already set -- the same reason ViewOnly's own wrapper
            uses it.
          */}
          <div style={{ display: "contents" }} {...editableIf(sections.evaluations)}>
          <AssetEvaluation
            ref={assetEvaluationRef}
            proposedRecords={
              readOnly ? NO_PROPOSED : addedRecords("asset_evaluation_records")
            }
            proposedDebtRecords={
              readOnly ? NO_PROPOSED : addedRecords("asset_debt_records")
            }
            /*
              And which rows of the asset's OWN list are the accepted
              suggestion, so they carry the Review mark there instead of
              arriving unannounced among readings the customer took.
            */
            proposedRecordKeys={
              readOnly ? EMPTY_KEYS : proposedKeys("asset_evaluation_records")
            }
            proposedDebtRecordKeys={
              readOnly ? EMPTY_KEYS : proposedKeys("asset_debt_records")
            }
            value={assetEvaluation}
            onChange={setAssetEvaluation}
            focus={focusEvaluation}
            records={asset.asset_evaluation_records}
            debtRecords={asset.asset_debt_records}
            onDeleteRecord={(record) =>
              deleteRecord("asset_evaluation_records", record)
            }
            onDeleteDebtRecord={(record) =>
              deleteRecord("asset_debt_records", record)
            }
            onSaveReading={saveReading}
            onSaveDebt={saveDebt}
            /* Whether the asset ALREADY holds a debt -- see the Save's own note. */
            debtCommitted={asset?.has_associated_debt === true}
            onSaveEntry={saveEntry}
            /*
              The evaluation fields are proposable too, and the panel owns
              them -- so it gets the same two helpers the fields above use,
              and places them beside its own labels.
            */
            renderProposal={review}
            proposalMark={reviewMark}
            proposedFields={proposedFields}
            customExposureOptions={
              userData.UserData?.financial_exposure_custom_options || []
            }
            disabled={userData.isFreezed}
          />
          </div>
          <div
            className={styles.main_data_upper}
            style={{ position: "relative", zIndex: "2" }}
          >
            {/*
              Who inherits this asset, and separately who advises on it. Both
              are grantable in the sharing panel, so both have to unlock here
              -- and neither did: their controls are chips and a picker rather
              than fields, so ViewOnly's `disabled` never reached them and
              only its inherited pointer-events was holding them shut.
            */}
            <div style={{ width: "100%" }} {...editableIf(sections.beneficiaries)}>
              {review(
                "beneficiaries",
                content?.editAssetText24,
                <div
                  className={styles.delay_period}
                  style={{ marginTop: "20px", marginBottom: "10px" }}
                >
                  <div className={styles.bold_title}>
                    {content.editAssetText24}
                  </div>
                  <Info message={content.editAssetText25} />
                </div>
              )}
              <div
                disabled={userData.isFreezed}
                className={`${styles.file_container} ${reviewMark(
                  "beneficiaries",
                  "block"
                )}`}
              >
                {asset?.beneficiaries?.map((beneficiary_id) => {
                  let name = "";
                  let priority;
                  let avatar = null;
                  userData.UserData["beneficiaries"].map((e, index) => {
                    if (e.user_id === beneficiary_id) {
                      const benefAvatar =
                        userData?.UserData?.beneficiaries?.filter(
                          (y) => y.user_id === e.user_id
                        )[0]?.profile_avatar;
                      avatar = benefAvatar;
                      name = e.name;
                      priority = e.priority;
                    }
                    return null;
                  });
                  return (
                    <Beneficiarytrustee
                      name={name}
                      priority={priority}
                      deleatable={true}
                      avatar={avatar}
                      onDelete={() =>
                        setAsset({
                          ...asset,
                          beneficiaries: asset.beneficiaries.filter(
                            (beneficiary_) => beneficiary_ !== beneficiary_id
                          ),
                        })
                      }
                    />
                  );
                })}
                {/*
                  No !readOnly here, deliberately, and the advisors section
                  below does the same. The two sections sit side by side, so a
                  partner missing the + on one of them reads as something
                  broken rather than as something withheld. It is inert either
                  way -- ViewOnly's pointer-events covers the whole region.
                */}
                {asset?.beneficiaries?.length <= 11 && (
                  <AddBeneficiary
                    setShow={() => null}
                    openBeneficiary={openBeneficiary}
                    alreadySelected={asset.beneficiaries}
                    user_type={"beneficiaries"}
                    onSelect={(beneficiary) => {
                      setAsset({
                        ...asset,
                        beneficiaries: [
                          ...asset.beneficiaries,
                          beneficiary.user_id,
                        ],
                      });
                    }}
                  />
                )}
              </div>
            </div>
            <div style={{ width: "100%" }} {...editableIf(sections.trustees)}>
              {review(
                "trustees",
                dublicatedcontent?.dublicatedText64,
                <div
                  className={styles.delay_period}
                  style={{ marginTop: "20px", marginBottom: "10px" }}
                >
                  <div className={styles.bold_title}>
                    {dublicatedcontent?.dublicatedText64}
                  </div>
                  <Info message={content.editAssetText26} />
                </div>
              )}
              <div
                disabled={userData.isFreezed}
                className={`${styles.file_container} ${reviewMark(
                  "trustees",
                  "block"
                )}`}
              >
                {asset?.trustees?.map((trustee_id) => {
                  let name = "";
                  let avatar = null;
                  userData.UserData["trustees"].map((e, index) => {
                    if (e.user_id === trustee_id) {
                      name = e.name;
                      const trusteeAvatar =
                        userData?.UserData?.trustees?.filter(
                          (y) => y.user_id === e.user_id
                        )[0]?.profile_avatar;

                      avatar = trusteeAvatar;
                    }
                    return null;
                  });

                  return (
                    <Beneficiarytrustee
                      name={name}
                      avatar={avatar}
                      priority={0}
                      deleatable={true}
                      onDelete={() =>
                        setAsset({
                          ...asset,
                          trustees: asset.trustees.filter(
                            (trustee_) => trustee_ !== trustee_id
                          ),
                        })
                      }
                    />
                  );
                })}
                {asset?.trustees.length <= 24 && (
                  <AddTrustee
                    openAssets={openAssets}
                    alreadySelected={asset.trustees}
                    user_type={"trustees"}
                    onSelect={(trustee) => {
                      setAsset({
                        ...asset,
                        trustees: [...asset.trustees, trustee.user_id],
                      });
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {/*
            Credentials, and the whole section goes for a partner.

            They have no flag and are never sent, so this drew an empty heading
            over empty fields -- an invitation to wonder what is behind it. There
            is nothing behind it. A partner creating an asset has no business
            here either: the endpoint strips credentials off anything they send.
          */}
          {!userData.partnerReadOnly && (
            <>
              <div
                className={styles.delay_period}
                style={{ marginTop: "20px", marginBottom: "10px" }}
              >
                <div className={styles.bold_title}>{content.editAssetText27}</div>
              </div>
              <div
                disabled={userData.isFreezed}
                style={{ position: "relative", zIndex: "1" }}
              >
                <CredentialsMenu
                  value={asset.credentials}
                  onChange={(value) => {
                    editAsset("credentials", JSON.stringify(value));
                    handleScroll();
                  }}
                  formErrors={formErrors}
                />
              </div>
              <div
                className={`${styles.error_message} ${
                  error.credentials !== "" ? styles.error_message_credentials : ""
                }`}
              >
                {error.credentials === "" ? "" : error.credentials}
              </div>
            </>
          )}

          {partner && (
            <PartnerSharing
              value={partnerSharing}
              onChange={setPartnerSharing}
              disabled={userData.isFreezed}
              partnerName={partner.name}
              partnerAvatar={partner.avatar}
            />
          )}

          {/*
            The partner's own copy of it: what they hold on this asset,
            stated rather than inferred from which fields happen to be live.

            Read-only twice over -- `disabled` on every switch and ViewOnly
            around the modal -- and a separate render because `getPartner`
            is null for them by design.

            Built from `toEdit` rather than from `partnerSharing`, which is
            seeded only when the customer's own flags are present and is
            empty on their side. The master is forced on: they are looking
            at the asset, so it was shared.
          */}
          {userData.partnerReadOnly && (
            <PartnerSharing
              viewer="partner"
              value={{
                share_with_partner: true,
                share_description: toEdit?.share_description === true,
                share_files: toEdit?.share_files === true,
                share_evaluations: toEdit?.share_evaluations === true,
                share_beneficiaries: toEdit?.share_beneficiaries === true,
                share_trusted_advisor: toEdit?.share_trusted_advisor === true,
                edit_description: toEdit?.edit_description === true,
                edit_files: toEdit?.edit_files === true,
                edit_evaluations: toEdit?.edit_evaluations === true,
                edit_beneficiaries: toEdit?.edit_beneficiaries === true,
                edit_trusted_advisor: toEdit?.edit_trusted_advisor === true,
              }}
              onChange={() => {}}
              disabled
            />
          )}

          <div
            className={`${styles.shadow_bottom} ${styles.shadow_bottom_edit} ${
              showBottomShadow ? styles.active : ""
            }`}
          />

        </div>
        </ViewOnly>

        <div className={styles.buttons_div}>
          {!readOnly && (
            <div
              onClick={() => {
                setShowConfirmation(true);
              }}
              className={styles.delete_button}
            >
              {content.editAssetText30}
            </div>
          )}
          <div onClick={onClose} className={styles.cancel_button}>
            {content.editAssetText31}
          </div>
          {(!readOnly || canPropose) && (
          <button
            disabled={userData.isFreezed}
            onClick={(e) => checkNotification(e)}
            className={styles.save_button}
          >
            {/*
              A partner is not saving, they are asking. The word matters: the
              customer's record does not change when this is pressed.
            */}
            {canPropose
              ? approvalContent?.partnerApprovalText23 || "Propose changes"
              : awaitingApproval
              ? approvalContent?.partnerApprovalText14 || content.editAssetText32
              : content.editAssetText32}
          </button>
          )}
        </div>

        {modal ? (
          <div style={{ position: "relative", zIndex: "5" }}>{modal}</div>
        ) : null}
      </div>

      {/*
        Outside the card, not inside the scrolling body where it used to live.

        Confirmation's backdrop is position:fixed and means to cover the
        viewport. Reported symptom was that it did not: the dim filled the
        scroll area alone, title and footer buttons stayed bright, and the
        dialog sat centred in a grey panel instead of over the page. That is a
        fixed descendant being clipped to an overflow ancestor, and it had two
        of them -- .popup_scrollable (auto) and .main_box (hidden). Whether a
        fixed element escapes those is not something every engine agrees on;
        rendered in Chrome it looked correct, so this was not reproducible
        there.

        Rather than chase which ancestor and which engine: here the only
        ancestor is .background, which has no overflow at all, so there is
        nothing left to clip it. z-index 40 clears .main_box's 30.
      */}
      {showConfirmation ? (
        <div style={{ position: "relative", zIndex: "40" }}>
          <Confirmation
            OnConfirm={() => {
              deleteAsset();
              window.scrollTo(0, 0);
            }}
            title={content.editAssetText28}
            text={`${content.editAssetText29} /${asset.asset_name}?`}
            onClose={() => setShowConfirmation(false)}
            cancel_button={dublicatedcontent?.dublicatedText184}
            confirm_button={dublicatedcontent?.delete}
          />
        </div>
      ) : null}
    </div>
  );
}

export default EditAsset;
